import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import SafeIcon from '../common/SafeIcon';
import siteConfig from '../data/siteConfig.json';
import { FiCheckCircle, FiExternalLink } from 'react-icons/fi';

const Contact = () => {
  const { lang, t } = useLanguage();
  const [formStatus, setFormStatus] = useState('');
  const [selectedIssue, setSelectedIssue] = useState('replacement');
  const issueOptions = [
    { value: 'replacement', label: t('services.items.replacement.title') },
    { value: 'repair', label: t('services.items.repair.title') },
    { value: 'side', label: t('services.items.side.title') },
    { value: 'rear', label: t('services.items.rear.title') },
    { value: 'sunroof', label: t('services.items.sunroof.title') },
    { value: 'oil', label: t('garageServices.sections.maintenance.items')[0] },
    { value: 'brakes', label: t('garageServices.sections.maintenance.items')[1] },
    { value: 'tires', label: t('garageServices.sections.tires.title') },
    { value: 'tpms', label: t('garageServices.sections.hardware.tpms') },
    { value: 'hardware', label: t('garageServices.sections.hardware.title') },
    { value: 'other', label: t('contactPage.form.other') }
  ];
  const tireSizes = t('garageServices.sections.tires.sizes');

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormStatus('success');
    e.target.reset();
    setSelectedIssue('replacement');
    setTimeout(() => setFormStatus(''), 5000);
  };

  return (
    <div className="bg-slate-50 min-h-screen py-12 md:py-24">
      <div className="container mx-auto px-6 max-w-7xl">
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-6">{t('contactPage.title')}</h1>
          <p className="text-lg md:text-xl text-slate-600 max-w-2xl mx-auto">{t('contactPage.subtitle')}</p>
        </div>

        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12">
          
          {/* Contact Info */}
          <div className="lg:col-span-4 space-y-8 min-w-0">
            <div className="bg-white p-6 sm:p-8 md:p-10 rounded-3xl shadow-sm border border-slate-100 min-w-0">
              <h3 className="text-2xl font-bold text-slate-900 mb-8">{siteConfig.business.name}</h3>
              
              <div className="space-y-8">
                {[
                  { label: 'Address', value: siteConfig.business.address, href: null },
                  { label: 'Phone', value: siteConfig.business.phoneDisplay, href: `tel:${siteConfig.business.phoneHref}` },
                  { label: 'Email', value: siteConfig.business.email, href: `mailto:${siteConfig.business.email}` },
                  { label: 'Hours', value: siteConfig.business.hours[lang], href: null }
                ].map((item, idx) => (
                  <div key={idx} className="min-w-0 border-b border-slate-100 last:border-b-0 pb-5 last:pb-0">
                    <div className="min-w-0">
                      <p className="text-xs font-black uppercase tracking-widest text-slate-400 mb-1">{item.label}</p>
                      {item.href ? (
                        <a href={item.href} className="text-base sm:text-lg font-bold text-slate-900 hover:text-glass-blue transition-colors block leading-tight break-words">
                          {item.value}
                        </a>
                      ) : (
                        <p className="text-base sm:text-lg font-bold text-slate-900 leading-tight break-words">{item.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-3xl overflow-hidden shadow-sm border border-slate-100 min-w-0">
              <div className="p-6 border-b border-slate-100 flex items-center justify-between gap-4">
                <h3 className="font-bold text-slate-900">{t('map.title')}</h3>
                <a
                  href={siteConfig.links.googleBusiness}
                  target="_blank"
                  rel="noreferrer"
                  className="text-glass-blue hover:text-blue-700 transition-colors"
                  aria-label={t('map.openGoogle')}
                >
                  <SafeIcon icon={FiExternalLink} className="text-xl" />
                </a>
              </div>
              <iframe
                title={t('map.title')}
                src={siteConfig.links.googleMapsEmbed}
                className="w-full aspect-video lg:aspect-square border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-8 min-w-0">
            <div className="bg-white p-6 sm:p-8 md:p-12 rounded-3xl shadow-xl border border-slate-100 min-w-0">
              <h2 className="text-3xl font-bold text-slate-900 mb-10">{t('common.requestQuote')}</h2>
              
              {formStatus === 'success' && (
                <motion.div 
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-8 p-6 bg-green-50 text-green-700 border border-green-200 rounded-2xl font-bold flex items-center gap-3"
                >
                  <SafeIcon icon={FiCheckCircle} className="text-2xl" />
                  {t('contactPage.form.success')}
                </motion.div>
              )}

              <form onSubmit={handleSubmit} className="space-y-8">
                <div className="grid md:grid-cols-2 gap-8">
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-slate-900 uppercase tracking-wider ml-1">{t('contactPage.form.name')}</label>
                    <input type="text" required className="w-full px-5 py-4 rounded-xl border border-slate-200 focus:ring-4 focus:ring-glass-blue/10 focus:border-glass-blue outline-none transition-all bg-slate-50/50" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-slate-900 uppercase tracking-wider ml-1">{t('contactPage.form.phone')}</label>
                    <input type="tel" required className="w-full px-5 py-4 rounded-xl border border-slate-200 focus:ring-4 focus:ring-glass-blue/10 focus:border-glass-blue outline-none transition-all bg-slate-50/50" />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-8">
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-slate-900 uppercase tracking-wider ml-1">{t('contactPage.form.email')}</label>
                    <input type="email" required className="w-full px-5 py-4 rounded-xl border border-slate-200 focus:ring-4 focus:ring-glass-blue/10 focus:border-glass-blue outline-none transition-all bg-slate-50/50" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-slate-900 uppercase tracking-wider ml-1">{t('contactPage.form.vehicle')}</label>
                    <input type="text" placeholder="e.g. Honda Civic 2020" required className="w-full px-5 py-4 rounded-xl border border-slate-200 focus:ring-4 focus:ring-glass-blue/10 focus:border-glass-blue outline-none transition-all bg-slate-50/50" />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-8">
                  <div className="space-y-2">
                     <label className="text-sm font-bold text-slate-900 uppercase tracking-wider ml-1">{t('contactPage.form.issue')}</label>
                     <select
                      value={selectedIssue}
                      onChange={(e) => setSelectedIssue(e.target.value)}
                      className="w-full px-5 py-4 rounded-xl border border-slate-200 focus:ring-4 focus:ring-glass-blue/10 focus:border-glass-blue outline-none transition-all bg-slate-50/50 appearance-none"
                    >
                        {issueOptions.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                     </select>
                  </div>
                  <div className="space-y-2">
                     <label className="text-sm font-bold text-slate-900 uppercase tracking-wider ml-1">{t('contactPage.form.insurance')}</label>
                     <div className="flex gap-8 py-3">
                        <label className="flex items-center gap-3 cursor-pointer group">
                          <input type="radio" name="insurance" value="yes" className="w-5 h-5 text-glass-blue border-slate-300 focus:ring-glass-blue" />
                          <span className="font-semibold text-slate-700 group-hover:text-glass-blue transition-colors">{t('contactPage.form.yes')}</span>
                        </label>
                        <label className="flex items-center gap-3 cursor-pointer group">
                          <input type="radio" name="insurance" value="no" defaultChecked className="w-5 h-5 text-glass-blue border-slate-300 focus:ring-glass-blue" />
                          <span className="font-semibold text-slate-700 group-hover:text-glass-blue transition-colors">{t('contactPage.form.no')}</span>
                        </label>
                     </div>
                  </div>
                </div>

                {selectedIssue === 'tires' && (
                  <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="space-y-2"
                  >
                    <label className="text-sm font-bold text-slate-900 uppercase tracking-wider ml-1">{t('contactPage.form.tireSize')}</label>
                    <select className="w-full px-5 py-4 rounded-xl border border-slate-200 focus:ring-4 focus:ring-glass-blue/10 focus:border-glass-blue outline-none transition-all bg-slate-50/50 appearance-none">
                      <option>{t('contactPage.form.selectTireSize')}</option>
                      {tireSizes.map((size) => (
                        <option key={size}>{size}</option>
                      ))}
                    </select>
                  </motion.div>
                )}

                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-900 uppercase tracking-wider ml-1">{t('contactPage.form.message')}</label>
                  <textarea rows="4" className="w-full px-5 py-4 rounded-xl border border-slate-200 focus:ring-4 focus:ring-glass-blue/10 focus:border-glass-blue outline-none transition-all resize-none bg-slate-50/50"></textarea>
                </div>

                <button type="submit" className="w-full bg-glass-blue hover:bg-blue-600 text-white font-black py-5 rounded-xl transition-all text-xl shadow-xl hover:shadow-glass-blue/40 active:scale-[0.98]">
                  {t('contactPage.form.submit')}
                </button>
              </form>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default Contact;
